import React, { FC, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import classnames from 'classnames';
import { BinIcon, EditIconBlue, InvoiceSettingsIcon, JobTimelineNoteIcon, VerticalListActionsIcon } from 'assets/icons';
import { CustomPopover } from 'components';
import { selectUserDetails } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks/typedHooks';
import DateUtils from 'utils/dateUtils';

// Define the type for timeline events
export interface TimelineEvent {
  event: string;
  timestamp: string;
  performedBy: string;
  title: string;
  subText?: Array<{ actionSummary: string }>;
  merchantId?: string;
}

interface TimelineProps {
  events: TimelineEvent[];
  limit?: number;
  timelineId: string;
  onEditNoteClick?: (event: TimelineEvent) => void;
  onDeleteNoteClick?: (event: TimelineEvent) => void;
  subTitleClassName?: string;
  titleClassName?: string;
  subTextClassName?: string;
  timelineClassName?: string;
}

const Timeline: FC<TimelineProps> = ({
  events,
  limit,
  timelineId,
  onEditNoteClick,
  onDeleteNoteClick,
  subTitleClassName,
  titleClassName,
  subTextClassName,
  timelineClassName
}) => {
  const [currentActionsPopoverIdState, setCurrentActionsPopoverIdState] = useState<number>(null);
  const currentUser = useAppSelector(selectUserDetails);

  const getTimelineIcon = (eventType: string) => {
    switch (eventType) {
      case 'MANAGE_ADJUSTMENT':
        return <InvoiceSettingsIcon className="path-stroke-current h-[18px] w-[18px] shrink-0 text-tertiaryText" />;
      case 'NOTE':
        return <JobTimelineNoteIcon className="shrink-0" />;
      default:
        return <InvoiceSettingsIcon className="path-stroke-current h-[18px] w-[18px] shrink-0 text-tertiaryText" />;
    }
  };

  const isListItemActionsVisible = (eventId: number) => {
    return eventId === currentActionsPopoverIdState;
  };

  const onCloseListItemActionsPopover = (eventId: number) => {
    if (currentActionsPopoverIdState === eventId) {
      setCurrentActionsPopoverIdState(null);
    }
  };

  const onToggleListItemActions = (eventId: number) => {
    if (eventId === currentActionsPopoverIdState) {
      setCurrentActionsPopoverIdState(null);
    } else {
      setCurrentActionsPopoverIdState(eventId);
    }
  };

  // Check if the current user can edit/delete the event
  const canModifyEvent = (timelineEvent: TimelineEvent): boolean => {
    // If currentTeamMemberName is not provided, don't allow modifications
    if (!currentUser) return false;

    // Only allow editing/deleting if the current team member is the one who created the event
    return timelineEvent.merchantId === currentUser.id && timelineEvent.event === 'NOTE';
  };

  // eslint-disable-next-line react/no-multi-comp
  const ListActionsAnchor: React.FC<{ isPanelOpen: boolean }> = ({ isPanelOpen }) => {
    return (
      <div id="payrun-timeline-more-options">
        <VerticalListActionsIcon
          height={16}
          width={4}
          className={twMerge(
            'mx-2 shrink-0 cursor-pointer',
            classnames({
              'fill-current text-secondary': isPanelOpen
            })
          )}
        />
      </div>
    );
  };

  // eslint-disable-next-line react/no-multi-comp
  const ListItemActionsMenu: FC<{ timelineEvent: TimelineEvent }> = ({ timelineEvent }) => {
    return (
      <div className="space-y-3 text-sbase font-semibold text-primaryText">
        {onEditNoteClick && (
          <div
            id={`${timelineId}-3dot-editnote`}
            role="presentation"
            onClick={e => {
              e.stopPropagation();
              onEditNoteClick(timelineEvent);
            }}
            className="flex cursor-pointer flex-row items-center gap-2.5">
            <EditIconBlue height={16} width={16} />
            <div>Edit note</div>
          </div>
        )}
        {onDeleteNoteClick && (
          <div
            id={`${timelineId}-3dot-delete-note`}
            onClick={() => onDeleteNoteClick(timelineEvent)}
            className="flex cursor-pointer flex-row items-center gap-2.5">
            <BinIcon />
            <div>Delete note</div>
          </div>
        )}
      </div>
    );
  };

  // Format the timestamp string to a human-readable format
  const formatTimestamp = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      return DateUtils.getDateInFormat({
        date,
        dateFormat: 'd MMM yyyy, h:mm aa'
      });
    } catch (error) {
      return timestamp;
    }
  };

  return (
    <div className={`customNormalScroll flex grow flex-col overflow-y-auto p-4 pb-5 ${timelineClassName}`}>
      {events?.map(
        (timelineEvent, index) =>
          (limit ? index < limit : true) && (
            <div key={`timeline-${index + 1}`} className="flex gap-x-2.5">
              <div className="flex flex-col items-center">
                {getTimelineIcon(timelineEvent.event)}
                {(limit && limit < events.length ? index !== limit - 1 : index !== events.length - 1) && (
                  <div className="h-full w-px bg-borderGray"></div>
                )}
              </div>
              <div className={`w-full ${(limit ? index !== limit - 1 : index !== events.length - 1) && 'pb-6'}`}>
                <div className="flex w-full items-center justify-between">
                  <div className={`text-17px font-semibold text-primaryText ${titleClassName}`}>
                    {timelineEvent.title || 'System'}
                  </div>
                  {(onEditNoteClick || onDeleteNoteClick) && canModifyEvent(timelineEvent) && (
                    <CustomPopover
                      anchorComponentId="payrun-timeline-anchor"
                      showArrow={true}
                      arrowOffset="right-3"
                      minWidth="min-w-[150px]"
                      highlightAnchor={false}
                      anchorComponent={ListActionsAnchor}
                      zIndex="z-[101]"
                      offset="-right-3"
                      margin="mt-1"
                      onClosePopover={() => onCloseListItemActionsPopover(index)}
                      show={isListItemActionsVisible(index)}
                      onTogglePopover={() => onToggleListItemActions(index)}>
                      {<ListItemActionsMenu timelineEvent={timelineEvent} />}
                    </CustomPopover>
                  )}
                </div>
                {timelineEvent.subText && timelineEvent.subText?.length > 0 && (
                  <div
                    className={`mt-2.5 max-w-[260px] whitespace-pre-wrap break-words text-sbase font-medium 
                      text-primaryText ${subTitleClassName}`}>
                    {timelineEvent.subText.map((item, idx) => (
                      <div key={`subtext-${idx}`}>{item.actionSummary}</div>
                    ))}
                  </div>
                )}
                <div className={`mt-2 text-sbase text-accentText ${subTextClassName}`}>
                  {formatTimestamp(timelineEvent.timestamp)}
                </div>
              </div>
            </div>
          )
      )}
    </div>
  );
};

export default Timeline;
